import { Inmate } from '@reliance/types/models/Inmate';
import { InmateContactResult } from '@reliance/types/models/InmateContact';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useInmateContactsByInmateId } from '../../hooks/InmateContact';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import AddEditModal from '../../inmate-contact/AddEditModal';
import InmateContactTable from '../../inmate-contact/Table';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';
import { useAlerts } from '../../alert/AlertContext';

const COLUMNS = [
  { id: 'customer', meta: { sortName: 'ani' } },
  { id: 'inmateStatus', meta: { sortName: 'inmateStatus' } },
  { id: 'customerStatus', meta: { sortName: 'customerStatus' } },
  { id: 'unreadCount', meta: { sortName: 'unreadCount' } },
  { id: 'countyBlocked' },
  { id: 'texting' },
  { id: 'audioCalling' },
  { id: 'videoCalling' },
  { id: 'options' },
];

type InmateContactsProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const InmateContacts: React.FC<InmateContactsProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;

  const [editingContact, setEditingContact] = useState<InmateContactResult | null>(null);
  const [showingAddDialog, setShowingAddDialog] = useState(false);
  const alerts = useAlerts();

  const baseRecord = useMemo(() => ({ inmateId: inmate.id }), [inmate.id]);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
    initialSorting: [{ id: 'customer', desc: false }],
  });

  const filters = useMemo(
    () => ({
      inmateId: inmate.id,
      page: tableState.mergedRecord.page as number,
      perPage: tableState.mergedRecord.perPage as number,
      orderBy: String(tableState.mergedRecord.orderBy ?? 'ani'),
    }),
    [inmate.id, tableState.mergedRecord]
  );

  const contactsResult = useInmateContactsByInmateId(
    null,
    filters
  ) as {
    result?: { data: InmateContactResult[]; total: number };
    loading: boolean;
    execute: (id: number) => void;
  };

  useEffect(() => {
    if (
      tabSelected &&
      inmate.id &&
      contactsResult.result !== undefined &&
      !contactsResult.loading
    ) {
      contactsResult.execute(inmate.id);
    }
  }, [tableState.mergedRecord]);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        contactsResult.execute(inmate.id);
      }
    }, [contactsResult, inmate.id]),
  });

  useNotifyTabLoading('contacts', contactsResult.loading);

  const handleAddEditClose = useCallback(
    (saved: boolean) => {
      setShowingAddDialog(false);
      setEditingContact(null);
      if (saved) {
        alerts.success('The contact was successfully saved');
        if (inmate.id) {
          contactsResult.execute(inmate.id);
        }
      }
    },
    [inmate.id, contactsResult]
  );

  const rows = contactsResult.result?.data ?? [];
  const total = contactsResult.result?.total ?? 0;
  const page = tableState.pagination.pageIndex + 1;
  const perPage = tableState.pagination.pageSize;

  return (
    <>
      <Button
        type="button"
        variant="primary"
        className="mb-3"
        onClick={() => setShowingAddDialog(true)}>
        Add Contact
      </Button>

      <AddEditModal
        inmate={inmate}
        inmateContact={editingContact?.contact}
        open={showingAddDialog || !!editingContact}
        onClose={handleAddEditClose}
      />

      <InmateContactTable
        rows={rows}
        loading={contactsResult.loading}
        page={page}
        totalPages={Math.ceil(total / perPage) || 1}
        total={total}
        perPage={perPage}
        serverPagination={tableState.serverPagination}
        onServerPaginationChange={tableState.onServerPaginationChange}
        serverSorting={tableState.serverSorting}
        onServerSortingChange={tableState.onServerSortingChange}
        serverColumnFilters={tableState.serverColumnFilters}
        onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
        inmate={inmate}
        onEditContact={(row: InmateContactResult) =>
          setEditingContact(row)
        }
      />
    </>
  );
};

export default InmateContacts;
